<script lang="ts">
  /**
   * The right-margin page peek.
   *
   * This component renders **blank ruled placeholder lines and nothing else**.
   * That constraint is a security boundary, not a styling choice
   * (ARCHITECTURE.md §6):
   *
   * * Upcoming task titles are never passed in, so they cannot appear in the DOM
   *   and cannot be read via DevTools.
   * * `aria-hidden="true"` keeps it out of the accessibility tree, so a screen
   *   reader cannot traverse it either.
   * * A CSS blur was explicitly rejected: it hides content visually while
   *   leaving it fully present and readable.
   *
   * It receives page GEOMETRY only — slot count and page number. The `Task`
   * type is not accepted as a prop at all, so the next page's content cannot
   * reach this subtree even by mistake.
   */
  interface Props {
    /** Slots on the next page, so the ruling lines up. Never task content. */
    slots: number;
    /** Rendered as a small label. Geometry, not content. */
    pageNumber?: number;
    /** Adds a decorative folded corner to this page edge. */
    dogEared?: boolean;
    /** Last skipped page number, shown as a compact geometry-only marker. */
    lastSkippedPageNumber?: number;
    which?: 'previous' | 'next';
  }

  let {
    slots,
    pageNumber,
    dogEared = false,
    lastSkippedPageNumber,
    which = 'next',
  }: Props = $props();
  const showLastSkippedPage = $derived(
    lastSkippedPageNumber !== undefined && lastSkippedPageNumber !== pageNumber,
  );

  const placeholders = $derived(Array.from({ length: slots }, (_, i) => i));
</script>

<!--
  aria-hidden plus inert: excluded from the accessibility tree AND from
  sequential focus navigation. `inert` is the belt to `aria-hidden`'s braces,
  because a focused element inside an aria-hidden subtree is an ARIA violation
  that some screen readers still announce.
-->
<div
  class="page-margin page-margin--{which}"
  class:dog-ear={dogEared}
  aria-hidden="true"
  inert
  data-testid="next-page-margin"
  data-slots={slots}
>
  {#if pageNumber !== undefined}
    <div
      class="margin-page-number"
      data-testid={dogEared ? `dog-eared-page-${pageNumber}` : undefined}
    >Page {pageNumber}</div>
  {/if}

  {#if showLastSkippedPage}
    <div
      class="margin-last-skipped"
      data-testid="dog-eared-page-{lastSkippedPageNumber}"
    >Page {lastSkippedPageNumber}</div>
  {/if}

  <!-- Blank ruled lines only. -->
  {#each placeholders as i (i)}
    <div class="margin-rule"></div>
  {/each}
</div>